import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    ArrowLeft, PencilSimple, Power, MapPin, Phone, Envelope, Star, Briefcase,
    UsersThree, UserPlus, Storefront, Plus, GraduationCap, IdentificationBadge,
    LinkSimple, Buildings, Receipt, ClockClockwise, Handshake, Sparkle, Trash,
    CheckCircle, ShieldWarning, Warning, ArrowRight, Crown, Calendar,
    NotePencil, MagnifyingGlass, ClipboardText, Clock, Camera, Newspaper,
    ChatCircleText, Link as LinkIcon, Copy, ArrowCounterClockwise,
    // iter100.57 — Badge module icons (24-key catalog · 6 categories)
    Medal, Trophy, SealCheck, UsersFour, ShieldCheck, Certificate,
    Diamond, Heart, ChatCircle, Stethoscope, Baby, ArrowsClockwise,
    BookOpen, Rocket, TestTube,
} from "@phosphor-icons/react";
import ContentEditor from "@/components/doctor/ContentEditor";
import { CommentRequiredDialog } from "@/components/admin/CommentRequiredDialog";
import { SessionsPanel } from "@/components/admin/coe/SessionsPanel";
import AdminManualInvoiceDialog from "@/components/admin/AdminManualInvoiceDialog";
import { statusLabel as getStatusLabel } from "@/lib/caseStatus";
import PhoneInput10 from "@/components/site/PhoneInput10";
import { toE164, isValid10Digit } from "@/lib/phone";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";
import { confirmDialog } from "@/lib/confirm";

/* ===========================================================================
   AdminDoctorProfile — Path B Phase A (2026-02-25)
   ---------------------------------------------------------------------------
   Dedicated profile / command-center page at /admin/doctors/:id. Replaces the
   3 dialogs (Edit / Clinic staff / Patients) that previously lived modally on
   /admin/doctors. Adds 4 new tabs that surface data which existed but had no
   UI:
     - Application: originating credentials paper-trail + B2B lead backlink
     - Cases & invoices: case counts + recent B2B-invoice summary
     - Audit log: chronological audit-trail entries for this entity
     - Decommission: soft-delete action (cascades to practice_locations + users)
   Six tabs total. URL: /admin/doctors/:id?tab=overview|staff|patients|application|billing|audit
   =========================================================================== */

const EDIT_FIELDS = [
    { key: "name", label: "Name", type: "text" },
    // iter100.275 — Admin can now update login-phone + WhatsApp for
    // locked-out doctors. Both flow through `provider_onboarding.
    // change_doctor_phone()` on the backend so `db.users.phone` (OTP
    // login credential) stays atomically in sync with `db.doctors.phone`.
    { key: "phone", label: "Login phone", type: "phone", help: "Doctor uses this to OTP-log-in. Changing it here immediately updates their login." },
    { key: "whatsapp_phone", label: "WhatsApp phone", type: "phone", help: "Optional. Where WhatsApp notifications land — often same as login." },
    { key: "specialization", label: "Specialization", type: "text" },
    { key: "clinic_name", label: "Clinic name", type: "text" },
    { key: "city", label: "City", type: "text" },
    { key: "address", label: "Address", type: "text" },
    /* iter97.32 — Image URL removed. Photos now flow through the dedicated
       upload widget below (POST /api/admin/doctors/{id}/photo). Leaving an
       editable "Image URL" text field caused the long-standing bug where
       saving any unrelated profile change would overwrite a previously
       uploaded avatar with the form's blank value. */
    { key: "years_experience", label: "Years of experience", type: "number" },
    { key: "rating", label: "Rating (0-5)", type: "number", step: "0.1" },
    { key: "consultation_fee", label: "Consultation fee (₹)", type: "number" },
    { key: "bio", label: "Bio", type: "textarea" },
];

const TABS = [
    { id: "overview",    label: "Overview",     icon: Briefcase },
    { id: "cases",       label: "Cases",        icon: ClipboardText },
    { id: "staff",       label: "Clinic staff", icon: UsersThree },
    { id: "patients",    label: "Patients",     icon: UserPlus },
    { id: "content",     label: "Public content", icon: Newspaper },
    { id: "application", label: "Application",  icon: GraduationCap },
    { id: "billing",     label: "Billing",      icon: Receipt },
    { id: "sessions",    label: "Sessions",     icon: Clock },
    { id: "audit",       label: "Audit log",    icon: ClockClockwise },
];

/* iter100.9 — Admin-side API for the reusable ContentEditor. Same UX as
   the doctor portal but hits `/admin/doctors/{id}/content/*` so any
   admin can override or bulk-add on behalf of any doctor. */
const adminContentApi = (doctorId) => ({
    list:      (kind) => `/admin/doctors/${doctorId}/content/${kind}`,
    createUrl: (kind) => `/admin/doctors/${doctorId}/content/${kind}`,
    itemUrl:   (id)   => `/admin/doctors/${doctorId}/content/item/${id}`,
    assetUrl:  ()     => `/admin/doctors/${doctorId}/content/asset`,
});

const initials = (n) => (n || "?").replace(/^Dr\.\s*/, "").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();


export default function AdminDoctorProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [doctor, setDoctor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("overview");
    const [toggleOpen, setToggleOpen] = useState(false);
    // iter100.71 — Reset profile UX. Admin opens a dialog, types a
    // reason (≥10 chars), confirms. Backend soft-archives every
    // transactional collection tagged with the batch_id so another
    // admin can restore within 30 days. History renders inline in
    // the Overview → "Reset history" strip.
    const [resetOpen, setResetOpen] = useState(false);
    const [resetHistoryBump, setResetHistoryBump] = useState(0);

    const load = () => {
        setLoading(true);
        api.get(`/admin/doctors/${id}`)
            .then((r) => setDoctor(r.data))
            .catch(() => setDoctor(null))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [id]);

    const toggleActive = async (comment) => {
        try {
            const r = await api.put(`/admin/doctors/${id}/toggle-active`, { comment });
            toast.success(`${doctor.name} ${r.data.is_active ? "activated" : "deactivated"}`);
            setToggleOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to toggle status");
            throw e;
        }
    };

    const toggleDci = async () => {
        const next = !doctor.dci_verified;
        const action = next
            ? `Mark ${doctor.name} as DCI verified? The "DCI verified" trust badge will appear on this provider everywhere they're public-listed (Find a Doctor, booking page, profile pages).`
            : `Remove the DCI verified badge from ${doctor.name}? It will disappear from every public surface immediately.`;
        if (!window.confirm(action)) return;
        try {
            await api.put(`/admin/doctors/${id}/dci-verification`, { verified: next });
            toast.success(next ? `${doctor.name} marked DCI verified` : `DCI verified removed from ${doctor.name}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to update DCI verification");
        }
    };

    // iter100.71 — Reset profile handler. Backend soft-archives every
    // transactional collection tagged with the batch_id and returns
    // the batch summary. We refresh the doctor row (cases_count /
    // patients_count in header) and bump the reset-history rev so the
    // Overview strip re-fetches.
    const doReset = async (reason) => {
        try {
            const r = await api.post(`/admin/doctors/${id}/reset`, { reason });
            const total = Object.values(r.data.counts || {}).reduce((a, b) => a + b, 0);
            toast.success(
                total > 0
                    ? `${doctor.name} reset — archived ${total} rows across ${Object.keys(r.data.counts).length} collections`
                    : `${doctor.name} reset — nothing to archive`
            );
            setResetOpen(false);
            setResetHistoryBump((x) => x + 1);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to reset doctor");
            throw e;
        }
    };

    if (loading && !doctor) return <div className="text-muted-foreground p-4">Loading…</div>;
    if (!doctor) return <DoctorNotFound onBack={() => navigate("/admin/doctors")} />;

    const isDecommissioned = !!doctor.decommissioned_at;

    return (
        <div data-testid="admin-doctor-profile" className="space-y-5">
            <Link to="/admin/doctors" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="profile-back-link">
                <ArrowLeft size={14} /> Back to providers
            </Link>

            {/* HEADER — iter101.32 · Matches PatientProfile grammar
                exactly. Only Deactivate lives in the header; DCI and
                Reset are secondary admin actions and live in the
                Overview tab as SectionCards (same pattern Patient uses
                for Decommission — dangerous/administrative actions are
                intentionally NOT in the header). */}
            <ProfileHeader
                doctor={doctor}
                onToggleActive={() => setToggleOpen(true)}
                isDecommissioned={isDecommissioned}
            />

            {/* TABS */}
            <AdminSegmentedTabs
                tabs={TABS.map((t) => ({
                    id: t.id,
                    label: t.label,
                    Icon: t.icon,
                    count:
                        t.id === "cases"    ? (doctor.cases_count ?? 0) :
                        t.id === "patients" ? (doctor.patients_count ?? 0) :
                        t.id === "staff"    ? (doctor.clinic_staff_count ?? 0) : null,
                }))}
                active={tab}
                onChange={setTab}
                testid="profile-tabs"
                tabTestidBase="profile-tab"
                showZero
            />

            {/* TAB BODY */}
            <div data-testid={`profile-tab-body-${tab}`}>
                {tab === "overview"    && <OverviewTab doctor={doctor} onSaved={load} isDecommissioned={isDecommissioned} onAfterDecommission={load} resetHistoryBump={resetHistoryBump} onResetRestored={load} onToggleDci={toggleDci} onReset={() => setResetOpen(true)} />}
                {tab === "cases"       && <CasesTab doctorId={id} />}
                {tab === "staff"       && <ClinicStaffTab doctorId={id} />}
                {tab === "patients"    && <PatientsTab doctorId={id} />}
                {tab === "content"     && <ContentTab doctorId={id} />}
                {tab === "application" && <ApplicationTab doctorId={id} />}
                {tab === "billing"     && <BillingTab doctorId={id} doctorName={doctor.name} />}
                {tab === "sessions"    && <SessionsPanel memberId={id} endpoint={`/admin/users/${id}/sessions`} />}
                {tab === "audit"       && <AuditTab doctorId={id} />}
            </div>

            <CommentRequiredDialog
                open={toggleOpen}
                onClose={() => setToggleOpen(false)}
                title={doctor.is_active === false ? `Activate ${doctor.name}?` : `Deactivate ${doctor.name}?`}
                description={doctor.is_active === false
                    ? "They will regain access to their portal immediately. Please note the reason for reactivation."
                    : "They will lose access to their portal immediately. All cases, patients, and history are preserved. Please note the reason for deactivation."}
                ctaLabel={doctor.is_active === false ? "Activate provider" : "Deactivate provider"}
                ctaVariant={doctor.is_active === false ? "default" : "destructive"}
                onConfirm={toggleActive}
            />

            {/* iter100.71 — Reset profile dialog. Mandatory reason ≥10 chars.
                Confirms with an unambiguous phone-match string ("RESET
                <doctor phone>") so the admin can't muscle-memory click
                through. Reset button also lives in the header. */}
            <ResetProfileDialog
                open={resetOpen}
                onClose={() => setResetOpen(false)}
                doctor={doctor}
                onConfirm={doReset}
            />
        </div>
    );
}


/* ===========================================================================
   HEADER — iter101.32 · Mirrors PatientProfile.jsx line-for-line.
   Simpler layout: avatar + identity block + QuickStats + one primary
   action (Deactivate). DCI verified + Reset profile moved out of the
   header into Overview → Admin actions card so the layout doesn't
   collapse under 6+ badges/actions like it used to.
   =========================================================================== */
function ProfileHeader({ doctor, onToggleActive, isDecommissioned }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-6 bento-shadow">
            <div className="flex items-start gap-5 flex-wrap">
                {/* Avatar — matches PatientProfile shape/size exactly */}
                <div className="w-20 h-20 rounded-2xl bg-primary/10 grid place-items-center font-display font-bold text-2xl text-primary shrink-0 overflow-hidden ring-4 ring-secondary/30">
                    {doctor.image ? (
                        <img src={doctor.image} alt={doctor.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                    ) : initials(doctor.name)}
                </div>

                {/* Identity — name + pills inline, then a compact 3-line contact block */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="profile-doctor-name">{doctor.name}</h1>
                        {isDecommissioned ? (
                            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-rose-200 bg-rose-100 text-rose-700 font-bold inline-flex items-center gap-1">
                                <Warning size={10} weight="fill" /> Decommissioned
                            </span>
                        ) : doctor.is_active ? (
                            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-100 text-emerald-700 font-bold">
                                Active
                            </span>
                        ) : (
                            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-amber-200 bg-amber-100 text-amber-700 font-bold">
                                Deactivated
                            </span>
                        )}
                        {doctor.is_new_partner && !doctor.rating && (
                            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-100 text-emerald-700 font-bold inline-flex items-center gap-1">
                                <Sparkle size={9} weight="fill" /> New partner
                            </span>
                        )}
                        {doctor.dci_verified && (
                            <span
                                className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 font-bold inline-flex items-center gap-1"
                                title={doctor.dci_verified_at ? `Verified ${new Date(doctor.dci_verified_at).toLocaleDateString()}${doctor.dci_verified_by ? ` by ${doctor.dci_verified_by}` : ""}` : "DCI verified"}
                                data-testid="profile-dci-verified-pill"
                            >
                                <CheckCircle size={10} weight="fill" /> DCI verified
                            </span>
                        )}
                    </div>
                    {doctor.specialization && (
                        <div className="text-[12px] text-primary font-medium mb-1.5 truncate">{doctor.specialization}</div>
                    )}
                    <div className="text-xs text-muted-foreground space-y-0.5 mt-1">
                        {doctor.clinic_name && (
                            <div className="flex items-center gap-1.5 min-w-0">
                                <Buildings size={12} className="shrink-0" />
                                <span className="truncate">{doctor.clinic_name}</span>
                            </div>
                        )}
                        {(doctor.address || doctor.city) && (
                            <div className="flex items-center gap-1.5 min-w-0">
                                <MapPin size={12} className="shrink-0" />
                                <span className="truncate">{[doctor.address, doctor.city].filter(Boolean).join(", ")}</span>
                            </div>
                        )}
                        {doctor.account?.phone && (
                            <div className="flex items-center gap-1.5">
                                <Phone size={12} className="shrink-0" />
                                <span className="font-mono">{doctor.account.phone}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick stat tiles — 3 tiles (Cases · Patients · Staff) */}
                <div className="grid grid-cols-3 gap-3">
                    <QuickStat icon={Briefcase} value={doctor.cases_count} label="Cases" />
                    <QuickStat icon={UserPlus} value={doctor.patients_count} label="Patients" />
                    <QuickStat icon={UsersThree} value={doctor.clinic_staff_count} label="Staff" />
                </div>

                {/* Actions — only Deactivate lives here (mirrors PatientProfile) */}
                {!isDecommissioned && (
                    <div className="flex items-center gap-2 self-start">
                        <Button
                            variant="outline" size="sm"
                            onClick={onToggleActive}
                            className={doctor.is_active ? "text-rose-600 border-rose-200 hover:bg-rose-50" : "text-emerald-700 border-emerald-200 hover:bg-emerald-50"}
                            data-testid="profile-toggle-active-btn"
                        >
                            <Power size={14} className="mr-1.5" />
                            {doctor.is_active ? "Deactivate" : "Activate"}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

const QuickStat = ({ icon: Icon, value, label }) => (
    <div className="bg-secondary/40 rounded-xl px-3 py-2 text-center min-w-[68px]">
        <Icon size={14} className="text-primary mx-auto mb-0.5" weight="duotone" />
        <div className="font-display font-bold text-lg leading-tight tabular-nums">{value ?? 0}</div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{label}</div>
    </div>
);


/* ===========================================================================
   iter97.32 — Profile photo uploader. Direct upload to
   POST /api/admin/doctors/{id}/photo (never touches the generic profile
   save), DELETE /api/admin/doctors/{id}/photo to clear. The proxy URL
   `/api/public/doctor-avatar/{id}?v=…` is what we render.
   =========================================================================== */
function DoctorPhotoUploader({ doctor, disabled, onChange }) {
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const pick = () => { if (!disabled && !busy) fileRef.current?.click(); };

    const upload = async (file) => {
        if (!file) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Use JPEG, PNG or WebP"); return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Image must be under 2 MB"); return;
        }
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/admin/doctors/${doctor.id}/photo`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Photo updated");
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Photo upload failed");
        } finally { setBusy(false); }
    };

    const clear = async () => {
        if (!doctor.image) return;
        if (!(await confirmDialog({ title: "Remove this doctor's profile photo?", confirmText: "Remove", tone: "danger" }))) return;
        setBusy(true);
        try {
            await api.delete(`/admin/doctors/${doctor.id}/photo`);
            toast.success("Photo cleared");
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't clear photo");
        } finally { setBusy(false); }
    };

    /* Cache-bust on doctor.image change so an admin sees their new upload
       immediately even though /public/doctor-avatar/{id} cached on edge. */
    const imgSrc = doctor.image ? `${doctor.image}${doctor.image.includes("?") ? "&" : "?"}_t=${doctor.updated_at || ""}` : null;
    const initialsLabel = initials(doctor.name);

    return (
        <div className="flex items-center gap-4 mb-5 pb-5 border-b border-border" data-testid="admin-doctor-photo-uploader">
            <div className="relative shrink-0">
                {imgSrc ? (
                    <img
                        src={imgSrc}
                        alt={doctor.name}
                        className="w-20 h-20 rounded-full object-cover ring-2 ring-primary/15"
                        data-testid="admin-doctor-photo-img"
                    />
                ) : (
                    <div
                        className="w-20 h-20 rounded-full bg-primary/10 text-primary grid place-items-center font-bold text-2xl ring-2 ring-primary/15"
                        data-testid="admin-doctor-photo-initials"
                    >
                        {initialsLabel || "?"}
                    </div>
                )}
                {busy && (
                    <div className="absolute inset-0 rounded-full bg-background/70 grid place-items-center">
                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                )}
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-foreground">Profile photo</div>
                <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                    JPEG, PNG or WebP · max 2 MB. Visible on Find-a-Doctor cards + invoice headers.
                </div>
                <div className="flex items-center gap-2 mt-2.5">
                    <Button
                        size="sm"
                        type="button"
                        variant="outline"
                        disabled={disabled || busy}
                        onClick={pick}
                        className="rounded-full"
                        data-testid="admin-doctor-photo-upload-btn"
                    >
                        <Camera size={13} className="mr-1.5" weight="fill" />
                        {doctor.image ? "Change photo" : "Upload photo"}
                    </Button>
                    {doctor.image && (
                        <Button
                            size="sm"
                            type="button"
                            variant="ghost"
                            disabled={disabled || busy}
                            onClick={clear}
                            className="rounded-full text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                            data-testid="admin-doctor-photo-clear-btn"
                        >
                            <Trash size={13} className="mr-1.5" />
                            Remove
                        </Button>
                    )}
                </div>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
                    className="hidden"
                    data-testid="admin-doctor-photo-input"
                />
            </div>
        </div>
    );
}


/* ===========================================================================
   TAB 1 — OVERVIEW: edit profile fields + decommission action
   =========================================================================== */
function OverviewTab({ doctor, onSaved, isDecommissioned, onAfterDecommission, resetHistoryBump, onResetRestored, onToggleDci, onReset }) {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [decommissionBusy, setDecommissionBusy] = useState(false);

    useEffect(() => {
        const next = {};
        EDIT_FIELDS.forEach((f) => { next[f.key] = doctor[f.key] ?? ""; });
        setForm(next);
    }, [doctor]);

    const dirty = useMemo(() => {
        return EDIT_FIELDS.some((f) => String(form[f.key] ?? "") !== String(doctor[f.key] ?? ""));
    }, [form, doctor]);

    const save = async () => {
        setSaving(true);
        try {
            // Coerce numerics; backend expects None for absent values (not "")
            const payload = {};
            EDIT_FIELDS.forEach((f) => {
                const v = form[f.key];
                if (f.type === "number") payload[f.key] = v === "" || v == null ? null : Number(v);
                else payload[f.key] = v;
            });
            await api.put(`/admin/doctors/${doctor.id}`, payload);
            toast.success("Profile updated");
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        }
        setSaving(false);
    };

    const decommission = async () => {
        setDecommissionBusy(true);
        try {
            await api.put(`/admin/doctors/${doctor.id}/decommission`);
            toast.success(`${doctor.name} decommissioned. Hidden from /find-doctor.`);
            setConfirming(false);
            onAfterDecommission?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to decommission");
        }
        setDecommissionBusy(false);
    };

    return (
        <>
        <div className="grid lg:grid-cols-3 gap-5">
            {/* LEFT — edit form */}
            <div className="lg:col-span-2 space-y-5">
                <SectionCard icon={PencilSimple} title="Public profile">
                    {isDecommissioned && (
                        <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 mb-4 text-sm text-rose-800" data-testid="profile-decommissioned-notice">
                            <Warning size={14} weight="fill" className="inline mr-1.5" />
                            This provider is decommissioned. Editing is disabled. Decommissioned by{" "}
                            <strong>{doctor.decommissioned_by || "—"}</strong> on{" "}
                            <strong>{doctor.decommissioned_at ? new Date(doctor.decommissioned_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</strong>.
                        </div>
                    )}
                    {/* iter97.32 — Profile photo upload. Replaces the legacy
                        editable "Image URL" text field which caused photos
                        to disappear when admins inadvertently saved a blank
                        value. Upload here writes via /api/admin/doctors/{id}/photo
                        directly — never piggy-backed onto the generic
                        profile save. */}
                    <DoctorPhotoUploader
                        doctor={doctor}
                        disabled={isDecommissioned}
                        onChange={onSaved}
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                        {EDIT_FIELDS.map((f) => (
                            <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                                <Label>{f.label}</Label>
                                {f.type === "textarea" ? (
                                    <Textarea
                                        value={form[f.key] ?? ""}
                                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                        rows={3} className="mt-1.5"
                                        disabled={isDecommissioned}
                                        data-testid={`profile-edit-${f.key}`}
                                    />
                                ) : f.type === "phone" ? (
                                    <div className="mt-1.5 h-11">
                                        <PhoneInput10
                                            value={form[f.key] ?? ""}
                                            onChange={(v) => setForm({ ...form, [f.key]: v })}
                                            testid={`profile-edit-${f.key}`}
                                            inputClassName="h-11"
                                            disabled={isDecommissioned}
                                        />
                                    </div>
                                ) : (
                                    <Input
                                        type={f.type}
                                        step={f.step}
                                        value={form[f.key] ?? ""}
                                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                        className="h-11 mt-1.5"
                                        disabled={isDecommissioned}
                                        data-testid={`profile-edit-${f.key}`}
                                    />
                                )}
                                {f.help && (
                                    <p className="text-[11px] text-muted-foreground mt-1">{f.help}</p>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-end gap-2 pt-3 border-t border-border mt-5">
                        <Button onClick={save} disabled={!dirty || saving || isDecommissioned} data-testid="profile-edit-save-btn">
                            {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </SectionCard>

                {/* iter100.32 — Landing-page URL / slug management.
                    Doctors cannot change their own slug (compliance
                    + brand safety); if they legally rename themselves
                    they contact Invisique support and admins update
                    the slug here. Old URLs auto-redirect to the new
                    one via previous_slugs[]. */}
                <AdminSlugEditor doctor={doctor} disabled={isDecommissioned} onSaved={onSaved} />

                {/* iter100.42 — Trust badges. Admin grants / revokes from
                    a fixed taxonomy (Featured 2026, Panel Member, ISO 9001,
                    5+ Years, Board Certified, Top Rated). Doctor can only
                    toggle visibility on their side. */}
                <AdminTrustBadges doctorId={doctor.id} disabled={isDecommissioned} />
            </div>

            {/* RIGHT — paper trail + dangerous actions */}
            <div className="space-y-5">
                <SectionCard icon={IdentificationBadge} title="Credentials paper-trail">
                    <dl className="text-sm space-y-2.5">
                        <KV label="Degree"       value={doctor.degree} />
                        <KV label="Council reg." value={doctor.council_registration_number} mono />
                        <KV label="State council" value={doctor.state_council} />
                        <KV label="Approved at"  value={fmtDate(doctor.approved_at)} />
                        <KV label="Onboarded at" value={fmtDate(doctor.onboarded_at)} />
                    </dl>
                </SectionCard>

                <SectionCard icon={Crown} title="Linked account">
                    <dl className="text-sm space-y-2.5">
                        <KV label="Phone"        value={doctor.account?.phone} mono />
                        <KV label="Email"        value={doctor.account?.email} />
                        <KV label="Last login"   value={fmtDate(doctor.account?.last_login_at)} />
                        <KV label="Login active" value={doctor.account?.is_active ? "Yes" : "No"} />
                    </dl>
                </SectionCard>

                {/* iter101.32 — Admin actions moved out of the header so
                    the profile shell mirrors PatientProfile's compact
                    layout. DCI verification is a trust toggle (safe to
                    flip); Reset profile is a heavier admin operation
                    (soft-archives every transactional record). Kept
                    together but visually distinct from Danger zone. */}
                {!isDecommissioned && (
                    <SectionCard icon={ShieldCheck} title="Admin actions">
                        <div className="space-y-3">
                            <div>
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold">DCI verification</div>
                                        <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                                            Shows the &ldquo;DCI verified&rdquo; trust badge on every public surface (Find a Doctor, microsite, booking).
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={onToggleDci}
                                        className={doctor.dci_verified
                                            ? "text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                            : "text-muted-foreground border-border hover:bg-secondary"}
                                        data-testid="profile-toggle-dci-btn"
                                        title={doctor.dci_verified
                                            ? "Remove the DCI verified trust badge"
                                            : "Mark this doctor as DCI verified"}
                                    >
                                        <CheckCircle size={13} weight={doctor.dci_verified ? "fill" : "regular"} className="mr-1.5" />
                                        {doctor.dci_verified ? "Verified" : "Mark verified"}
                                    </Button>
                                </div>
                            </div>
                            <div className="pt-3 border-t border-border">
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold">Reset profile</div>
                                        <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                                            Soft-archives every patient, case, appointment, invoice, chat and notification. Identity + published content stay intact. Reversible within 30 days.
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={onReset}
                                        className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                        data-testid="profile-reset-btn"
                                    >
                                        <ArrowCounterClockwise size={13} className="mr-1.5" />
                                        Reset
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </SectionCard>
                )}

                {!isDecommissioned && (
                    <SectionCard icon={Trash} title="Danger zone" tone="rose">
                        <p className="text-xs text-muted-foreground mb-3">
                            Decommission permanently removes this provider from <code className="px-1 bg-secondary rounded">/find-doctor</code>,
                            hides all practice locations, and disables logins for the doctor and clinic staff. Cases, patients,
                            and invoices are <strong>not</strong> deleted — they remain for audit and financial records.
                        </p>
                        {!confirming ? (
                            <Button
                                variant="outline" size="sm"
                                onClick={() => setConfirming(true)}
                                className="text-rose-700 border-rose-200 hover:bg-rose-50"
                                data-testid="profile-decommission-btn"
                            >
                                <Trash size={14} className="mr-1.5" /> Decommission provider
                            </Button>
                        ) : (
                            <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 space-y-2.5" data-testid="profile-decommission-confirm">
                                <p className="text-xs text-rose-900">
                                    Confirm decommission for <strong>{doctor.name}</strong>? This cascade is reversible only by
                                    direct DB intervention.
                                </p>
                                <div className="flex items-center justify-end gap-2">
                                    <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} data-testid="profile-decommission-cancel-btn">Cancel</Button>
                                    <Button size="sm" onClick={decommission} disabled={decommissionBusy} className="bg-rose-600 hover:bg-rose-700" data-testid="profile-decommission-confirm-btn">
                                        {decommissionBusy ? "Decommissioning…" : "Yes, decommission"}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </SectionCard>
                )}
            </div>
        </div>

            {/* iter100.71 — Reset history strip. Only visible when this
                doctor has ≥1 past reset batch; otherwise renders null.
                Restore button appears when the 30-day retention window
                is still open. */}
            <ResetHistoryStrip
                doctorId={doctor.id}
                doctorName={doctor.name}
                refreshKey={resetHistoryBump}
                onRestored={onResetRestored}
            />
        </>
    );
}


/* ===========================================================================
   TAB 2 — CASES (read-only roster of every case this doctor has submitted to HQ)
   =========================================================================== */
const CASE_STATUS_STYLES = {
    draft:               "bg-secondary text-foreground border-border",
    submitted:           "bg-sky-100 text-sky-700 border-sky-200",
    in_review:           "bg-violet-100 text-violet-700 border-violet-200",
    planning:            "bg-indigo-100 text-indigo-700 border-indigo-200",
    plan_review:         "bg-violet-100 text-violet-700 border-violet-200",
    approved:            "bg-emerald-100 text-emerald-700 border-emerald-200",
    in_production:       "bg-amber-100 text-amber-800 border-amber-200",
    shipped:             "bg-cyan-100 text-cyan-800 border-cyan-200",
    delivered:           "bg-teal-100 text-teal-800 border-teal-200",
    in_treatment:        "bg-blue-100 text-blue-700 border-blue-200",
    completed:           "bg-emerald-100 text-emerald-700 border-emerald-200",
    rejected:            "bg-rose-100 text-rose-700 border-rose-200",
    cancelled:           "bg-rose-100 text-rose-700 border-rose-200",
};

/* AdminSlugEditor — admin-only landing URL/slug management for a doctor.
   Doctors cannot self-serve; they contact Invisique support and an admin
   uses this form to update the slug. Previous slug is auto-preserved in
   `previous_slugs[]` so any legacy links / QR codes still redirect. */
function AdminSlugEditor({ doctor, disabled, onSaved }) {
    const [status, setStatus] = useState(null);
    const [editing, setEditing] = useState(false);
    const [newSlug, setNewSlug] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get(`/admin/doctors/${doctor.id}/landing-slug`);
            setStatus(data);
        } catch { /* silent */ }
    };
    useEffect(() => { if (doctor?.id) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [doctor?.id]);

    const origin = typeof window !== "undefined" ? window.location.origin : "";

    if (!status) {
        return (
            <SectionCard icon={LinkIcon} title="Public landing URL">
                <div className="text-sm text-muted-foreground">Loading…</div>
            </SectionCard>
        );
    }

    const fullUrl = `${origin}/s/${status.current_slug}`;
    const nameMismatch = !status.matches_name && status.suggested_slug && status.suggested_slug !== status.current_slug;

    const openEditor = (initial) => {
        setNewSlug(initial ?? status.current_slug);
        setEditing(true);
        setError(null);
    };
    const cancelEditor = () => {
        setEditing(false);
        setNewSlug("");
        setError(null);
    };

    const submit = async () => {
        const clean = (newSlug || "").trim().toLowerCase();
        if (!/^[a-z][a-z0-9-]{2,59}$/.test(clean)) {
            setError("3-60 chars, must start with a letter, lowercase letters / digits / hyphens only.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await api.put(`/admin/doctors/${doctor.id}/landing-slug`, { new_slug: clean });
            toast.success("URL updated. Old link still redirects.");
            setEditing(false);
            await load();
            onSaved?.();
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not update URL");
        } finally {
            setSaving(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(fullUrl);
            toast.success("URL copied");
        } catch {
            toast.error("Couldn't copy");
        }
    };

    return (
        <SectionCard icon={LinkIcon} title="Public landing URL">
            <p className="text-xs text-muted-foreground mb-3 leading-snug">
                Doctors cannot change this themselves. Update here when a provider legally renames themselves and requests a URL match. Old URLs auto-redirect to the new one.
            </p>

            <div className="flex items-center gap-2 p-3 rounded-xl border border-border bg-secondary/40" data-testid="admin-slug-current">
                <LinkIcon size={16} weight="duotone" className="text-primary shrink-0" />
                <div className="flex-1 min-w-0 font-mono text-[13px] truncate">
                    {origin}<span className="text-primary font-semibold">/s/{status.current_slug}</span>
                </div>
                <Button size="sm" variant="ghost" onClick={copy} title="Copy URL" data-testid="admin-slug-copy">
                    <Copy size={13} />
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEditor()} disabled={disabled} data-testid="admin-slug-edit-btn">
                    <PencilSimple size={13} /> Change URL
                </Button>
            </div>

            {nameMismatch && !editing && (
                <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-start gap-3" data-testid="admin-slug-suggestion">
                    <Warning size={18} weight="duotone" className="text-amber-700 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <div className="text-sm font-semibold text-amber-900">Slug doesn&apos;t match name</div>
                        <div className="text-[12px] text-amber-800 mt-0.5 leading-snug">
                            Current: <code className="font-mono">/s/{status.current_slug}</code> · Suggested from name: <code className="font-mono font-semibold">/s/{status.suggested_slug}</code>
                        </div>
                        <div className="mt-2">
                            <Button
                                size="sm"
                                onClick={() => openEditor(status.suggested_slug)}
                                disabled={disabled}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid="admin-slug-adopt-suggestion"
                            >
                                Use {status.suggested_slug}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {editing && (
                <div className="mt-3 rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="admin-slug-form">
                    <div>
                        <Label className="text-sm">New URL</Label>
                        <div className="mt-1 flex items-stretch">
                            <span className="inline-flex items-center px-3 border border-r-0 border-border rounded-l-lg bg-secondary/60 text-[13px] text-muted-foreground font-mono">
                                {origin}/s/
                            </span>
                            <Input
                                value={newSlug}
                                onChange={(e) => setNewSlug(e.target.value.toLowerCase())}
                                placeholder="dr-name"
                                className="rounded-l-none font-mono text-[13px]"
                                data-testid="admin-slug-input"
                                autoFocus
                            />
                        </div>
                        <div className="mt-1 text-[11px] text-muted-foreground">
                            3-60 chars · lowercase letters, digits and hyphens · must start with a letter
                        </div>
                        {error && (
                            <div className="mt-1 text-[12px] text-red-700 flex items-center gap-1">
                                <Warning size={12} weight="fill" /> {error}
                            </div>
                        )}
                    </div>
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900 leading-snug flex items-start gap-2">
                        <Warning size={14} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
                        <div>
                            <strong>Heads up:</strong> Google may take 4-8 weeks to re-index. Any printed QR codes/business cards using the old URL still work — they redirect here.
                        </div>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={cancelEditor} disabled={saving} data-testid="admin-slug-cancel">
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={submit}
                            disabled={saving || !newSlug || newSlug === status.current_slug}
                            data-testid="admin-slug-save"
                        >
                            {saving ? "Saving…" : "Save new URL"}
                        </Button>
                    </div>
                </div>
            )}

            {status.previous_slugs?.length > 0 && (
                <details className="mt-3" data-testid="admin-slug-history">
                    <summary className="text-[12px] text-muted-foreground cursor-pointer hover:text-foreground">
                        {status.previous_slugs.length} previous URL{status.previous_slugs.length === 1 ? "" : "s"} (all still redirect here)
                    </summary>
                    <ul className="mt-2 space-y-1 text-[12px] text-muted-foreground font-mono pl-4">
                        {status.previous_slugs.map((p) => (
                            <li key={p.slug} className="flex items-center gap-2">
                                <span className="line-through">/s/{p.slug}</span>
                                <span className="text-[10px] text-muted-foreground/70">
                                    changed {p.changed_at ? new Date(p.changed_at).toLocaleDateString() : "recently"}
                                </span>
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </SectionCard>
    );
}


/* ==========================================================================
   AdminTrustBadges (iter100.57)
   Admin grants / revokes trust badges from a 24-key catalog spanning six
   categories (Milestone · Clinical · Performance · Recognition · Volume ·
   Community). Six of the 24 are AUTO-DERIVED (5-year veteran, Smile
   Clubs, high completion, most reviewed) — they're read-only in the UI.
   The other 18 are admin-grantable via a category-filtered picker.
   Grants soft-delete on revoke (grant history preserved) and support
   expiry via `expires_at` (auto-set for Featured Provider + Provider of
   the Year).
   ========================================================================== */

// Icon map covers every icon key emitted by the backend catalog. Missing
// keys fall through to Medal (the safe default).
const ADMIN_BADGE_ICON_MAP = {
    Star, UsersFour, Trophy, SealCheck, Medal, ShieldCheck, Certificate,
    Sparkle, Diamond, MapPin, Heart, CheckCircle, ChatCircle, Stethoscope,
    Baby, ArrowsClockwise, GraduationCap, BookOpen, Rocket, TestTube,
};
function AdminBadgeIcon({ name, size = 14 }) {
    const C = ADMIN_BADGE_ICON_MAP[name] || Medal;
    return <C size={size} weight="fill" />;
}

// Per-category palette used only inside this section (not on the public
// microsite identity strip — that keeps the 3-bucket rose/teal/green
// convention decided earlier). Each hex is intentionally desaturated so
// six categories co-exist without visual competition.
const CATEGORY_PALETTES = {
    milestone:   { bg: "hsl(215 75% 96%)", border: "hsl(215 60% 85%)", text: "hsl(215 65% 32%)", accent: "hsl(215 70% 42%)" },
    clinical:    { bg: "hsl(192 75% 96%)", border: "hsl(192 55% 85%)", text: "hsl(192 65% 28%)", accent: "hsl(192 70% 38%)" },
    performance: { bg: "hsl(35 80% 96%)",  border: "hsl(35 70% 84%)",  text: "hsl(35 70% 32%)",  accent: "hsl(35 85% 45%)" },
    recognition: { bg: "hsl(340 75% 97%)", border: "hsl(340 55% 88%)", text: "hsl(340 55% 38%)", accent: "hsl(340 65% 48%)" },
    volume:      { bg: "hsl(275 60% 97%)", border: "hsl(275 45% 88%)", text: "hsl(275 45% 38%)", accent: "hsl(275 55% 48%)" },
    community:   { bg: "hsl(150 50% 96%)", border: "hsl(150 40% 85%)", text: "hsl(150 55% 28%)", accent: "hsl(150 55% 38%)" },
};

function AdminTrustBadges({ doctorId, disabled }) {
    // active: [{id, badge_key, badge_name, category, icon, granted_at,
    //           granted_by_name, expires_at, auto_derived, is_active, ...}]
    // catalog: [{key, name, category, icon, auto_derived, admin_grantable,
    //            part_of_mutex_group}]
    // categories: [{key, label}]
    const [active, setActive] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedKeys, setSelectedKeys] = useState(new Set());

    const load = async () => {
        try {
            const [listRes, catRes] = await Promise.all([
                api.get(`/admin/doctors/${doctorId}/badges`),
                api.get(`/admin/badges/catalog`),
            ]);
            setActive(listRes.data.active || []);
            setCatalog(catRes.data.catalog || []);
            setCategories(catRes.data.categories || []);
        } catch {
            /* silent */
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [doctorId]);

    const activeKeys = useMemo(
        () => new Set((active || []).map((b) => b.badge_key)),
        [active],
    );

    // Badges available in the picker: admin-grantable AND not currently held.
    // Auto-derived stay OUT of the picker entirely (they show up in the
    // active grid with a "Auto-detected" pill, revocation-disabled).
    const pickerCandidates = useMemo(
        () => (catalog || []).filter(
            (b) => b.admin_grantable && !activeKeys.has(b.key),
        ),
        [catalog, activeKeys],
    );
    const filteredCandidates = useMemo(
        () => selectedCategory
            ? pickerCandidates.filter((b) => b.category === selectedCategory)
            : pickerCandidates,
        [pickerCandidates, selectedCategory],
    );

    const toggleKey = (key) => {
        setSelectedKeys((s) => {
            const n = new Set(s);
            if (n.has(key)) n.delete(key); else n.add(key);
            return n;
        });
    };

    const grant = async () => {
        if (selectedKeys.size === 0) return;
        try {
            await api.post(`/admin/doctors/${doctorId}/badges`, {
                keys: Array.from(selectedKeys),
            });
            const noun = selectedKeys.size === 1 ? "Badge" : `${selectedKeys.size} badges`;
            toast.success(`${noun} granted`);
            setPickerOpen(false);
            setSelectedKeys(new Set());
            setSelectedCategory("");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to grant");
        }
    };

    const revoke = async (badge) => {
        if (badge.auto_derived) return;  // shouldn't happen — UI guards
        const label = badge.badge_name || badge.badge_key;
        if (!window.confirm(`Are you sure you want to revoke "${label}"?`)) return;
        try {
            await api.delete(`/admin/doctors/${doctorId}/badges/${badge.id}`);
            toast.success("Badge revoked");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to revoke");
        }
    };

    // Group active badges by category for the display grid.
    const grouped = useMemo(() => {
        const m = new Map();
        (active || []).forEach((b) => {
            const key = b.category || "other";
            if (!m.has(key)) m.set(key, []);
            m.get(key).push(b);
        });
        return m;
    }, [active]);

    return (
        <SectionCard
            icon={Medal}
            title="Badges"
            right={!disabled && !pickerOpen && (
                <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)} data-testid="admin-badge-grant-btn">
                    <Plus size={12} /> Grant badge
                </Button>
            )}
        >
            {loading ? (
                <div className="text-sm text-muted-foreground italic">Loading…</div>
            ) : (
                <>
                    <p className="text-xs text-muted-foreground mb-3">
                        Trust &amp; achievement badges awarded by Invisique™. Public profile renders active badges organised by category (max 8). Auto-derived badges (5-Year Veteran, Smile Clubs, High Completion, Most Reviewed) are computed from live data and can&apos;t be manually granted or revoked.
                    </p>

                    {pickerOpen && (
                        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 mb-4" data-testid="admin-badge-picker">
                            <div className="flex items-baseline justify-between gap-3 mb-3">
                                <Label className="text-xs font-bold">Grant badges</Label>
                                <div className="text-[11px] text-muted-foreground">{selectedKeys.size} selected</div>
                            </div>

                            {/* Category filter dropdown */}
                            <Label className="text-[11px] font-semibold text-slate-700">Category</Label>
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                                data-testid="admin-badge-category-select"
                            >
                                <option value="">All categories ({pickerCandidates.length})</option>
                                {categories.map((c) => {
                                    const count = pickerCandidates.filter((b) => b.category === c.key).length;
                                    return (
                                        <option key={c.key} value={c.key} disabled={count === 0}>
                                            {c.label} ({count})
                                        </option>
                                    );
                                })}
                            </select>

                            {/* Multi-select checkbox grid */}
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                                {filteredCandidates.length === 0 && (
                                    <div className="col-span-full text-[12px] text-muted-foreground italic py-4 text-center">
                                        Nothing available in this category — either all granted or admin-grantable options exhausted.
                                    </div>
                                )}
                                {filteredCandidates.map((b) => {
                                    const on = selectedKeys.has(b.key);
                                    const p = CATEGORY_PALETTES[b.category] || CATEGORY_PALETTES.milestone;
                                    return (
                                        <button
                                            key={b.key}
                                            type="button"
                                            onClick={() => toggleKey(b.key)}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all ${on ? "ring-2 ring-primary/40 border-primary bg-white" : "border-border hover:border-primary/40 bg-white"}`}
                                            data-testid={`admin-badge-picker-${b.key}`}
                                            aria-pressed={on}
                                        >
                                            <span
                                                className="h-6 w-6 rounded-md flex items-center justify-center shrink-0 border"
                                                style={{ backgroundColor: p.bg, borderColor: p.border, color: p.accent }}
                                            >
                                                <AdminBadgeIcon name={b.icon} />
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold text-slate-800 truncate">{b.name}</div>
                                                <div className="text-[10px] text-muted-foreground uppercase tracking-wider" style={{ color: p.text }}>
                                                    {b.category}
                                                </div>
                                            </div>
                                            {on && <CheckCircle size={14} weight="fill" className="text-primary shrink-0" />}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="mt-4 flex justify-end gap-2">
                                <Button size="sm" variant="ghost" onClick={() => { setPickerOpen(false); setSelectedKeys(new Set()); setSelectedCategory(""); }}>
                                    Cancel
                                </Button>
                                <Button size="sm" onClick={grant} disabled={selectedKeys.size === 0} data-testid="admin-badge-grant-confirm">
                                    Grant {selectedKeys.size > 0 ? `(${selectedKeys.size})` : ""}
                                </Button>
                            </div>
                        </div>
                    )}

                    {(active || []).length === 0 && !pickerOpen && (
                        <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-6 text-center text-sm text-muted-foreground italic" data-testid="admin-badge-empty">
                            No badges yet.
                            {!disabled && <> Click &ldquo;Grant badge&rdquo; above to award one.</>}
                        </div>
                    )}

                    {/* Active badges grid, grouped by category */}
                    {(active || []).length > 0 && (
                        <div className="space-y-4" data-testid="admin-badge-list">
                            {categories.map((c) => {
                                const rows = grouped.get(c.key) || [];
                                if (rows.length === 0) return null;
                                const p = CATEGORY_PALETTES[c.key] || CATEGORY_PALETTES.milestone;
                                return (
                                    <div key={c.key}>
                                        <div className="text-[10px] font-bold uppercase tracking-[0.16em] mb-2" style={{ color: p.text }}>
                                            {c.label} · {rows.length}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {rows.map((b) => (
                                                <BadgeCard
                                                    key={b.id}
                                                    badge={b}
                                                    palette={p}
                                                    onRevoke={disabled ? null : revoke}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </SectionCard>
    );
}

function BadgeCard({ badge, palette, onRevoke }) {
    const isAuto = badge.auto_derived;
    const grantedTxt = badge.granted_at
        ? new Date(badge.granted_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
        : (isAuto ? "Auto-detected" : "");
    const expiredSoon = badge.expires_at
        ? new Date(badge.expires_at) < new Date(Date.now() + 30 * 24 * 3600 * 1000)
        : false;
    return (
        <div
            className="group relative flex items-start gap-3 py-2.5 px-3 rounded-lg border transition-colors"
            style={{ borderColor: palette.border, backgroundColor: palette.bg }}
            data-testid={`admin-badge-row-${badge.badge_key}`}
        >
            <span
                className="h-8 w-8 rounded-md flex items-center justify-center shrink-0 mt-0.5 bg-white border"
                style={{ borderColor: palette.border, color: palette.accent }}
            >
                <AdminBadgeIcon name={badge.icon} size={16} />
            </span>
            <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                    {badge.badge_name}
                    {isAuto && (
                        <span className="text-[9px] font-bold uppercase tracking-wider bg-white text-slate-600 border border-slate-200 px-1.5 rounded">
                            Auto
                        </span>
                    )}
                </div>
                <div className="text-[10.5px] text-slate-600 mt-0.5">
                    {grantedTxt}
                    {badge.granted_by_name && badge.granted_by_name !== "auto" ? ` · by ${badge.granted_by_name}` : ""}
                    {badge.expires_at ? ` · expires ${new Date(badge.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : ""}
                    {expiredSoon && !isAuto ? " · ⚠ expiring soon" : ""}
                </div>
            </div>
            {onRevoke && !isAuto && (
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onRevoke(badge)}
                    className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`admin-badge-revoke-${badge.badge_key}`}
                >
                    <Trash size={12} />
                </Button>
            )}
        </div>
    );
}


function CasesTab({ doctorId }) {
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/doctors/${doctorId}/cases`)
            .then((r) => setCases(r.data))
            .finally(() => setLoading(false));
    }, [doctorId]);

    const stats = useMemo(() => {
        const counts = {};
        cases.forEach((c) => { counts[c.status] = (counts[c.status] || 0) + 1; });
        return counts;
    }, [cases]);

    const filtered = useMemo(() => {
        let list = cases;
        if (statusFilter !== "all") list = list.filter((c) => c.status === statusFilter);
        if (q.trim()) {
            const l = q.toLowerCase();
            list = list.filter((c) =>
                (c.code || "").toLowerCase().includes(l) ||
                (c.patient_name || "").toLowerCase().includes(l) ||
                (c.patient_code || "").toLowerCase().includes(l)
            );
        }
        return list;
    }, [cases, statusFilter, q]);

    // Build filter chip options dynamically from observed statuses (cheaper than
    // hardcoding the full enum). "all" always present, others sorted by count.
    const filterChips = useMemo(() => {
        const sortedStatuses = Object.keys(stats).sort((a, b) => stats[b] - stats[a]);
        return [{ id: "all", label: "All", count: cases.length }, ...sortedStatuses.map((s) => ({
            id: s, label: s.replace(/_/g, " "), count: stats[s],
        }))];
    }, [stats, cases.length]);

    return (
        <SectionCard icon={ClipboardText} title={`Case roster (${cases.length})`} right={
            <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code or patient…" className="pl-9 h-9 w-56" data-testid="profile-cases-search" />
            </div>
        }>
            {/* Status filter chips */}
            {filterChips.length > 1 && (
                <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="profile-cases-filter">
                    {filterChips.map((c) => {
                        const on = statusFilter === c.id;
                        return (
                            <button
                                key={c.id}
                                onClick={() => setStatusFilter(c.id)}
                                className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors capitalize ${on ? "bg-primary text-white border-primary" : "border-border hover:bg-secondary"}`}
                                data-testid={`profile-cases-filter-${c.id}`}
                            >
                                {c.label}
                                <span className={`ml-1.5 tabular-nums ${on ? "text-white/70" : "text-muted-foreground"}`}>{c.count}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
            {!loading && cases.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8" data-testid="profile-cases-empty">
                    This provider hasn&apos;t sent any cases yet. New case submissions appear here automatically.
                </div>
            )}
            {!loading && cases.length > 0 && filtered.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8">No cases match your search.</div>
            )}

            <div className="space-y-2">
                {filtered.map((c) => {
                    const statusCls = CASE_STATUS_STYLES[c.status] || "bg-secondary text-foreground border-border";
                    return (
                        <Link
                            key={c.id}
                            to={`/admin/cases/${c.id}`}
                            className="flex items-center gap-3 p-3 border border-border rounded-xl hover:bg-secondary/30 hover:border-primary/30 transition"
                            data-testid={`profile-case-${c.id}`}
                        >
                            <ClipboardText size={16} className="text-primary shrink-0" weight="duotone" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-sm font-semibold">{c.code}</span>
                                    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border capitalize ${statusCls}`}>
                                        {getStatusLabel(c.status)}
                                    </span>
                                    {c.stage_number != null && (
                                        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-border bg-secondary text-muted-foreground">
                                            Stage {c.stage_number}
                                            {c.total_stages ? `/${c.total_stages}` : ""}
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                    {c.patient_name && c.patient_name !== "—" ? c.patient_name : "Patient: —"}
                                    {c.patient_code && <span className="ml-1.5 font-mono opacity-70">({c.patient_code})</span>}
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="text-[11px] text-muted-foreground">
                                    {c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                                </div>
                            </div>
                            <ArrowRight size={14} className="text-muted-foreground shrink-0" />
                        </Link>
                    );
                })}
            </div>
        </SectionCard>
    );
}


/* ===========================================================================
   TAB 3 — CLINIC STAFF (extracted from ClinicStaffDialog body — now inline)
   =========================================================================== */
function ClinicStaffTab({ doctorId }) {
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ name: "", phone: "", email: "" });
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        api.get(`/admin/doctors/${doctorId}/clinic-staff`)
            .then((r) => setStaff(r.data))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [doctorId]);

    const addMember = async () => {
        if ((form.name || "").trim().length < 2) return toast.error("Enter a valid name");
        if (!isValid10Digit(form.phone)) return toast.error("Enter their 10-digit Indian mobile number");
        setSaving(true);
        try {
            const r = await api.post(`/admin/doctors/${doctorId}/clinic-staff`, { ...form, phone: toE164(form.phone) });
            setStaff((prev) => [r.data, ...prev]);
            setForm({ name: "", phone: "", email: "" });
            setCreating(false);
            toast.success("Team member added");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
        setSaving(false);
    };

    const toggleActive = async (s) => {
        try {
            const r = await api.put(`/admin/clinic-staff/${s.id}/toggle-active`);
            setStaff((prev) => prev.map((x) => x.id === s.id ? { ...x, is_active: r.data.is_active } : x));
            toast.success(`${s.name} ${r.data.is_active ? "enabled" : "disabled"}`);
        } catch {
            toast.error("Failed");
        }
    };

    return (
        <SectionCard icon={UsersThree} title="Clinic coordinators" right={
            !creating && (
                <Button size="sm" onClick={() => setCreating(true)} data-testid="profile-staff-add-btn">
                    <Plus size={14} className="mr-1.5" /> Add coordinator
                </Button>
            )
        }>
            {creating && (
                <div className="p-4 bg-secondary/40 rounded-xl border border-border space-y-2 mb-4" data-testid="profile-staff-form">
                    <div>
                        <Label className="text-xs">Name</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 mt-1" data-testid="profile-staff-form-name" />
                    </div>
                    <div>
                        <Label className="text-xs">Phone (10-digit Indian mobile)</Label>
                        <div className="h-9 mt-1">
                            <PhoneInput10
                                value={form.phone}
                                onChange={(v) => setForm({ ...form, phone: v })}
                                inputClassName="h-9"
                                testid="profile-staff-form-phone"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Email (optional)</Label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9 mt-1" data-testid="profile-staff-form-email" />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button size="sm" variant="ghost" onClick={() => { setCreating(false); setForm({ name: "", phone: "", email: "" }); }} disabled={saving}>Cancel</Button>
                        <Button size="sm" onClick={addMember} disabled={saving} data-testid="profile-staff-form-save">{saving ? "Saving…" : "Add"}</Button>
                    </div>
                </div>
            )}

            {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
            {!loading && staff.length === 0 && !creating && (
                <div className="text-sm text-muted-foreground text-center py-8">
                    No coordinators yet. Add one to help run this clinic.
                </div>
            )}
            <div className="space-y-2">
                {staff.map((s) => (
                    <div key={s.id} className={`flex items-center gap-3 p-3 border border-border rounded-xl ${s.is_active ? "" : "opacity-60"}`} data-testid={`profile-staff-${s.id}`}>
                        <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs shrink-0">
                            {(s.name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm flex items-center gap-2">
                                <span className="truncate">{s.name}</span>
                                {!s.is_active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 uppercase font-semibold">Disabled</span>}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                                <span className="flex items-center gap-1"><Phone size={11} /> {s.phone}</span>
                                {s.email && <span className="flex items-center gap-1 truncate"><Envelope size={11} /> {s.email}</span>}
                            </div>
                        </div>
                        <Button
                            size="sm" variant="outline"
                            onClick={() => toggleActive(s)}
                            className={s.is_active ? "text-rose-600 border-rose-200 hover:bg-rose-50" : "text-emerald-700 border-emerald-200 hover:bg-emerald-50"}
                            data-testid={`profile-staff-toggle-${s.id}`}
                        >
                            <Power size={14} className="mr-1" />
                            {s.is_active ? "Disable" : "Enable"}
                        </Button>
                    </div>
                ))}
            </div>
        </SectionCard>
    );
}


/* ===========================================================================
   TAB 3 — PATIENTS (extracted from DoctorPatientsDialog body — now inline)
   =========================================================================== */
const EMPTY_PATIENT = { name: "", phone: "", email: "", dob: "", gender: "", notes: "" };
const GENDERS = ["", "Female", "Male", "Other", "Prefer not to say"];

function PatientsTab({ doctorId }) {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState(EMPTY_PATIENT);
    const [saving, setSaving] = useState(false);
    const [q, setQ] = useState("");

    const load = () => {
        setLoading(true);
        api.get(`/admin/doctors/${doctorId}/patients`)
            .then((r) => setPatients(r.data))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [doctorId]);

    const filtered = patients.filter((p) => {
        if (!q) return true;
        const l = q.toLowerCase();
        return (p.name || "").toLowerCase().includes(l) || (p.phone || "").includes(q);
    });

    const submit = async () => {
        if ((form.name || "").trim().length < 2) return toast.error("Enter a valid name");
        if (!isValid10Digit(form.phone)) return toast.error("Enter the patient's 10-digit Indian mobile number");
        setSaving(true);
        try {
            const r = await api.post(`/admin/doctors/${doctorId}/patients`, { ...form, phone: toE164(form.phone) });
            setPatients((prev) => {
                if (prev.find((p) => p.id === r.data.id)) return prev;
                return [{ ...r.data, source: "clinic" }, ...prev];
            });
            setForm(EMPTY_PATIENT);
            setCreating(false);
            toast.success(`${r.data.name} added`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
        setSaving(false);
    };

    return (
        <SectionCard icon={UserPlus} title={`Patient roster (${patients.length})`} right={
            <div className="flex items-center gap-2">
                <div className="relative">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 h-9 w-44" data-testid="profile-patients-search" />
                </div>
                {!creating && (
                    <Button size="sm" onClick={() => setCreating(true)} data-testid="profile-patient-add-btn">
                        <Plus size={14} className="mr-1.5" /> Add
                    </Button>
                )}
            </div>
        }>
            {creating && (
                <div className="mt-1 p-4 bg-secondary/40 rounded-xl border border-border space-y-2 mb-4" data-testid="profile-patient-form">
                    <div>
                        <Label className="text-xs">Full name</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 mt-1" data-testid="profile-patient-form-name" />
                    </div>
                    <div>
                        <Label className="text-xs">Phone (10-digit Indian mobile)</Label>
                        <div className="h-9 mt-1">
                            <PhoneInput10
                                value={form.phone}
                                onChange={(v) => setForm({ ...form, phone: v })}
                                inputClassName="h-9"
                                testid="profile-patient-form-phone"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Email (optional)</Label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9 mt-1" data-testid="profile-patient-form-email" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs">DOB</Label>
                            <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="h-9 mt-1" />
                        </div>
                        <div>
                            <Label className="text-xs">Gender</Label>
                            <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="w-full h-9 mt-1 text-sm rounded-lg border border-border bg-background px-2">
                                {GENDERS.map((g) => <option key={g} value={g}>{g || "Select…"}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button size="sm" variant="ghost" onClick={() => { setCreating(false); setForm(EMPTY_PATIENT); }} disabled={saving}>Cancel</Button>
                        <Button size="sm" onClick={submit} disabled={saving} data-testid="profile-patient-form-save">{saving ? "Saving…" : "Add"}</Button>
                    </div>
                </div>
            )}

            {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
            {!loading && filtered.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8">No patients yet.</div>
            )}
            <div className="space-y-2">
                {filtered.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 p-3 border border-border rounded-xl" data-testid={`profile-patient-${p.id}`}>
                        <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs shrink-0">
                            {(p.name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm flex items-center gap-2">
                                <span className="truncate">{p.name}</span>
                                {/* iter98.63 — Walk-in badge gated on the
                                    explicit `is_walk_in` flag, not the
                                    overloaded `source === "clinic"`. See
                                    Patients.jsx for the full rationale. */}
                                {p.is_walk_in === true && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 uppercase font-semibold inline-flex items-center gap-1">
                                        <Storefront size={10} weight="fill" /> Walk-in
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                                <span className="flex items-center gap-1"><Phone size={11} /> {p.phone}</span>
                                {(p.visits ?? 0) > 0 && <span>· {p.visits} visit{p.visits === 1 ? "" : "s"}</span>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </SectionCard>
    );
}


/* ===========================================================================
   TAB 4 — APPLICATION (read-only credentials paper-trail + b2b lead backlink)
   =========================================================================== */
function ApplicationTab({ doctorId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/doctors/${doctorId}/application`)
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, [doctorId]);

    if (loading) return <SectionCard icon={GraduationCap} title="Application"><div className="text-sm text-muted-foreground">Loading…</div></SectionCard>;
    const app = data?.application;
    const lead = data?.b2b_lead;

    if (!app) {
        return (
            <SectionCard icon={GraduationCap} title="Application">
                <div className="text-sm text-muted-foreground text-center py-8" data-testid="profile-app-empty">
                    This provider was seeded directly (no originating application). Credentials would be on a real application from a future onboard.
                </div>
            </SectionCard>
        );
    }

    return (
        <div className="space-y-5" data-testid="profile-app-content">
            {lead && (
                <SectionCard icon={Handshake} title="Originating B2B lead" tone="primary">
                    <div className="grid sm:grid-cols-3 gap-x-6 gap-y-2.5 text-sm">
                        <KV label="Clinic"          value={lead.clinic} />
                        <KV label="Contact"         value={lead.contact_name} />
                        <KV label="Sales rep"       value={lead.assigned_sales_name || "—"} />
                        <KV label="Source"          value={lead.source} />
                        <KV label="Status"          value={lead.status} />
                        <KV label="Created"         value={fmtDate(lead.created_at)} />
                    </div>
                    <div className="mt-4 pt-3 border-t border-border">
                        <Link to={`/admin/b2b-leads/${lead.id}`} className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="profile-app-lead-link">
                            View full lead pipeline → <ArrowRight size={12} />
                        </Link>
                    </div>
                </SectionCard>
            )}

            <SectionCard icon={GraduationCap} title="Credentials at apply-time">
                <div className="grid sm:grid-cols-3 gap-x-6 gap-y-2.5 text-sm">
                    <KV label="Degree"             value={app.degree} />
                    <KV label="Specialty"          value={app.specialty} />
                    <KV label="Years in practice"  value={app.years_in_practice} />
                    <KV label="Council reg."       value={app.council_registration_number} mono />
                    <KV label="State council"      value={app.state_council} />
                    {app.linkedin_url && (
                        <KV label="LinkedIn" value={<a href={app.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">{app.linkedin_url}</a>} />
                    )}
                </div>
                {app.board_certifications && (
                    <div className="mt-4 pt-3 border-t border-border">
                        <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">Board certifications</div>
                        <p className="text-sm whitespace-pre-wrap">{app.board_certifications}</p>
                    </div>
                )}
            </SectionCard>

            <SectionCard icon={Briefcase} title="Practice & goals">
                <div className="grid sm:grid-cols-3 gap-x-6 gap-y-2.5 text-sm">
                    <KV label="Cases/month claimed" value={app.cases_per_month} />
                    <KV label="Current brand"       value={app.current_aligner_brand} />
                    <KV label="Preferred fee"       value={app.preferred_consultation_fee != null ? `₹${app.preferred_consultation_fee}` : "—"} />
                    <KV label="GSTIN"               value={app.gst_number} mono />
                    <KV label="Demographic"         value={app.primary_patient_demographic} />
                </div>
                {(app.why_invisique || app.message) && (
                    <div className="mt-4 pt-3 border-t border-border">
                        <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">Why Invisique™</div>
                        <p className="text-sm whitespace-pre-wrap">{app.why_invisique || app.message}</p>
                    </div>
                )}
                {app.internal_review_notes && (
                    <div className="mt-4 pt-3 border-t border-border bg-amber-50 -mx-5 -mb-5 px-5 py-3 rounded-b-2xl">
                        <div className="text-[10px] uppercase tracking-wide font-bold text-amber-800 mb-1">Internal review notes (admin only)</div>
                        <p className="text-sm whitespace-pre-wrap">{app.internal_review_notes}</p>
                        <div className="text-[11px] text-muted-foreground mt-1.5">
                            By {app.internal_review_notes_by || "?"} · {fmtDate(app.internal_review_notes_at)}
                        </div>
                    </div>
                )}
            </SectionCard>
        </div>
    );
}


/* ===========================================================================
   TAB 5 — BILLING (read-only summary + deep-link to full /admin/billing view)
   =========================================================================== */
function BillingTab({ doctorId, doctorName }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [manualOpen, setManualOpen] = useState(false);

    const load = () => {
        setLoading(true);
        api.get(`/admin/doctors/${doctorId}/billing-summary`)
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [doctorId]);

    if (loading) return <SectionCard icon={Receipt} title="Billing"><div className="text-sm text-muted-foreground">Loading…</div></SectionCard>;

    const inr = (v) => v == null ? "—" : `₹${Number(v).toLocaleString("en-IN")}`;
    const statusCls = (s) => {
        if (s === "paid")          return "bg-emerald-100 text-emerald-700 border-emerald-200";
        if (s === "partially_paid")return "bg-amber-100 text-amber-700 border-amber-200";
        if (s === "overdue")       return "bg-rose-100 text-rose-700 border-rose-200";
        if (s === "void")          return "bg-secondary text-muted-foreground border-border";
        return "bg-sky-100 text-sky-700 border-sky-200";
    };

    return (
        <div className="space-y-5" data-testid="profile-billing-content">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <SummaryCard icon={Receipt} value={inr(data.outstanding_total)} label="Outstanding" tone={data.outstanding_total > 0 ? "amber" : "default"} />
                <SummaryCard icon={Warning} value={data.overdue_count} label="Overdue invoices" tone={data.overdue_count > 0 ? "rose" : "default"} />
                <SummaryCard icon={ClockClockwise} value={data.total_invoices} label="Total invoices" />
            </div>

            <SectionCard icon={Receipt} title={`Recent invoices (${data.recent_invoices.length})`} right={
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        onClick={() => setManualOpen(true)}
                        className="rounded-full h-7 px-3 text-xs"
                        data-testid="profile-billing-new-invoice"
                    >
                        <Plus size={11} weight="bold" className="mr-1" /> New invoice
                    </Button>
                    <Link to="/admin/billing" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="profile-billing-full-link">
                        Full billing view → <ArrowRight size={11} />
                    </Link>
                </div>
            }>
                {data.recent_invoices.length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-8">No invoices for this provider yet.</div>
                ) : (
                    <div className="space-y-2">
                        {data.recent_invoices.map((inv) => {
                            const balance = (inv.total || 0) - (inv.amount_paid || 0);
                            return (
                                <div key={inv.id} className="flex items-center gap-3 p-3 border border-border rounded-xl" data-testid={`profile-billing-row-${inv.id}`}>
                                    <Receipt size={16} className="text-primary shrink-0" weight="duotone" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-sm font-semibold">{inv.code || inv.id.slice(0, 8)}</span>
                                            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${statusCls(inv.status)}`}>
                                                {inv.status}
                                            </span>
                                        </div>
                                        <div className="text-[11px] text-muted-foreground mt-0.5">
                                            Issued {fmtDate(inv.issued_at || inv.created_at)}
                                            {inv.due_at && ` · Due ${fmtDate(inv.due_at)}`}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="font-display font-bold text-sm tabular-nums">{inr(inv.total)}</div>
                                        {balance > 0 && <div className="text-[11px] text-amber-700 tabular-nums">{inr(balance)} due</div>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </SectionCard>

            <AdminManualInvoiceDialog
                open={manualOpen}
                onOpenChange={setManualOpen}
                onCreated={load}
                presetDoctorId={doctorId}
                presetDoctorName={doctorName}
            />
        </div>
    );
}


/* ===========================================================================
   TAB 6 — AUDIT LOG (chronological entries for this entity)
   =========================================================================== */
function AuditTab({ doctorId }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/doctors/${doctorId}/audit-log`)
            .then((r) => setEntries(r.data))
            .finally(() => setLoading(false));
    }, [doctorId]);

    return (
        <SectionCard icon={ClockClockwise} title={`Audit log (${entries.length})`}>
            {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
            {!loading && entries.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8" data-testid="profile-audit-empty">
                    No audit entries yet. Any admin action (edit, toggle-active, decommission, staff add, etc.) will show up here.
                </div>
            )}
            <ul className="space-y-3" data-testid="profile-audit-list">
                {entries.map((e) => (
                    <li key={e.id} className="flex gap-3" data-testid={`profile-audit-${e.id}`}>
                        <div className="shrink-0 w-8 h-8 rounded-full bg-secondary grid place-items-center text-xs font-bold text-muted-foreground">
                            {(e.actor_name || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                <div className="text-xs font-semibold">
                                    {e.actor_name || "System"}
                                    <span className="ml-2 text-[10px] uppercase tracking-wide font-bold text-muted-foreground">{(e.action || "").replace(/_/g, " ")}</span>
                                </div>
                                <div className="text-[11px] text-muted-foreground tabular-nums">{fmtDateTime(e.created_at)}</div>
                            </div>
                            {e.summary && <p className="text-sm mt-0.5">{e.summary}</p>}
                            {e.meta && Object.keys(e.meta).length > 0 && (
                                <pre className="text-[11px] font-mono text-muted-foreground mt-1 break-all whitespace-pre-wrap">{JSON.stringify(e.meta)}</pre>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </SectionCard>
    );
}


/* ===========================================================================
   Atoms
   =========================================================================== */
function SectionCard({ icon: Icon, title, right, tone, children }) {
    const cls = tone === "rose"
        ? "bg-rose-50 border-rose-200"
        : tone === "primary"
        ? "bg-primary/5 border-primary/20"
        : "bg-card border-border";
    return (
        <div className={`rounded-2xl border p-5 ${cls}`}>
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className={`flex items-center gap-2 ${tone === "rose" ? "text-rose-800" : tone === "primary" ? "text-primary" : ""}`}>
                    <Icon size={16} weight="duotone" />
                    <span className="text-xs uppercase tracking-wide font-bold">{title}</span>
                </div>
                {right}
            </div>
            {children}
        </div>
    );
}

function KV({ label, value, mono }) {
    return (
        <div>
            <dt className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-0.5">{label}</dt>
            <dd className={mono ? "font-mono text-sm break-all" : "text-sm break-words"}>
                {value === null || value === undefined || value === "" ? <span className="text-muted-foreground">—</span> : value}
            </dd>
        </div>
    );
}

function SummaryCard({ icon: Icon, value, label, tone }) {
    const cls = tone === "amber" ? "border-amber-200 bg-amber-50"
        : tone === "rose" ? "border-rose-200 bg-rose-50"
        : "border-border bg-card";
    const iconCls = tone === "amber" ? "text-amber-700" : tone === "rose" ? "text-rose-700" : "text-primary";
    return (
        <div className={`rounded-2xl border p-4 ${cls}`}>
            <Icon size={18} weight="duotone" className={`${iconCls} mb-2`} />
            <div className="font-display font-bold text-2xl tabular-nums">{value}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
        </div>
    );
}

function DoctorNotFound({ onBack }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-10 text-center">
            <Warning size={32} className="mx-auto mb-2 text-muted-foreground" />
            <h2 className="font-display text-xl font-bold mb-1">Provider not found.</h2>
            <p className="text-sm text-muted-foreground mb-5">It may have been decommissioned or never existed.</p>
            <Button onClick={onBack} variant="outline" className="rounded-full">Back to providers</Button>
        </div>
    );
}

/* iter100.9 — Admin-side content editor tab. Hosts 3 sub-tabs (Articles /
   Case Studies / Testimonials) that let an admin add or edit any doctor's
   public-page content. Same ContentEditor as the doctor portal — just a
   different apiBase so writes go to /admin/doctors/{id}/content/*. */
function ContentTab({ doctorId }) {
    const [sub, setSub] = React.useState("article");
    const api = React.useMemo(() => adminContentApi(doctorId), [doctorId]);
    const SUBS = [
        { id: "article",     label: "Articles",     icon: Newspaper },
        { id: "case_study",  label: "Case Studies", icon: Sparkle },
        { id: "testimonial", label: "Testimonials", icon: ChatCircleText },
    ];
    return (
        <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-display text-lg font-bold flex items-center gap-2">
                        <Newspaper size={18} weight="duotone" className="text-primary" /> Public-page content
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
                        Articles, case studies, and patient testimonials shown on the doctor&apos;s public page (<code>/s/&lt;slug&gt;</code>). Admins can add or override on behalf of the doctor here.
                    </p>
                </div>
            </div>
            <AdminSegmentedTabs
                tabs={SUBS.map((s) => ({ id: s.id, label: s.label, Icon: s.icon }))}
                active={sub}
                onChange={setSub}
                testid="admin-content-subtabs"
                tabTestidBase="admin-content-subtab"
            />
            <div className="pt-2">
                <ContentEditor kind={sub} apiBase={api} />
            </div>
        </div>
    );
}

const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (s) => s ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";


/* ===========================================================================
   iter100.71 — Reset profile dialog

   Destructive but reversible action. Backend soft-archives every
   transactional collection tagged with a batch_id; admin can restore
   within 30 days via the reset-history strip.

   Guardrails:
     - Reason ≥10 chars (matches backend validation, avoids server round-trip)
     - Confirm-phrase gate: admin types "RESET" verbatim to enable the CTA
     - Explicit scope callout so the admin knows exactly what dies vs what
       stays — reduces "wait, what did I just do" panic
   =========================================================================== */
function ResetProfileDialog({ open, onClose, doctor, onConfirm }) {
    const [reason, setReason] = useState("");
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) { setReason(""); setConfirmText(""); }
    }, [open]);

    if (!open || !doctor) return null;

    const reasonOk = reason.trim().length >= 10;
    const confirmOk = confirmText.trim().toUpperCase() === "RESET";
    const canSubmit = reasonOk && confirmOk && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            await onConfirm(reason.trim());
        } catch { /* toast handled upstream */ }
        finally { setBusy(false); }
    };

    return (
        <div
            className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
            data-testid="admin-reset-profile-dialog"
            onClick={onClose}
        >
            <div
                className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-4 py-3.5 border-b border-border flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                        <ArrowCounterClockwise size={16} weight="duotone" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="font-display font-bold text-[15px] leading-tight">Reset {doctor.name}?</h3>
                        <p className="text-[12px] text-muted-foreground mt-0.5 leading-snug">
                            Transactional data <span className="font-semibold text-foreground">soft-archives</span> and stays restorable for 30 days. Identity, credentials, and login are untouched.
                        </p>
                    </div>
                </div>

                <div className="px-4 py-3 space-y-3">
                    <div className="rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2 text-[11.5px] text-rose-900 leading-snug">
                        <div className="font-semibold flex items-center gap-1 mb-0.5">
                            <Warning size={12} weight="fill" />
                            Will be archived
                        </div>
                        Patients · Cases · Appointments · Invoices · Payments · Patient chat · Escalations · Lab chat · Notifications
                    </div>
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-[11.5px] text-emerald-900 leading-snug">
                        <div className="font-semibold flex items-center gap-1 mb-0.5">
                            <CheckCircle size={12} weight="fill" />
                            Stays intact
                        </div>
                        Name · DCI · Credentials · Letterhead · Business hours · Landing page · Badges · Credits · Login
                    </div>

                    <div>
                        <Label className="text-[11.5px] font-semibold flex items-center justify-between">
                            <span>Reason (mandatory, ≥10 characters)</span>
                            <span className={`text-[11px] font-normal ${reasonOk ? "text-emerald-600" : "text-muted-foreground"}`}>
                                {reason.trim().length}/10
                            </span>
                        </Label>
                        <Textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Fresh dashboard after pilot demo — test cases no longer needed."
                            rows={2}
                            className="mt-1 text-[13px]"
                            data-testid="admin-reset-reason-input"
                        />
                    </div>

                    <div>
                        <Label className="text-[11.5px] font-semibold">
                            Type <span className="font-mono font-bold text-rose-700">RESET</span> to enable
                        </Label>
                        <Input
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="RESET"
                            className="mt-1 h-9 font-mono uppercase tracking-widest text-sm"
                            data-testid="admin-reset-confirm-input"
                        />
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="admin-reset-cancel-btn">
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={submit}
                        disabled={!canSubmit}
                        data-testid="admin-reset-confirm-btn"
                    >
                        {busy ? "Resetting…" : "Reset profile"}
                    </Button>
                </div>
            </div>
        </div>
    );
}


/* ===========================================================================
   iter100.71 — Reset history strip

   Renders on the OverviewTab. Shows every past reset batch for this doctor
   with an inline Restore button when the 30-day window is still open.
   =========================================================================== */
function ResetHistoryStrip({ doctorId, doctorName, refreshKey, onRestored }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyBatchId, setBusyBatchId] = useState(null);

    const load = () => {
        setLoading(true);
        api.get(`/admin/doctors/${doctorId}/reset-history`)
            .then((r) => setRows(r.data || []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [doctorId, refreshKey]);

    const restore = async (batch) => {
        const total = Object.values(batch.counts || {}).reduce((a, b) => a + b, 0);
        if (!window.confirm(
            `Restore ${total} rows for ${doctorName}?\n\nEverything archived by this reset will be moved back into the live collections. Cannot be undone once restored.`
        )) return;
        setBusyBatchId(batch.id);
        try {
            await api.post(`/admin/reset-batches/${batch.id}/restore`);
            toast.success(`${doctorName} restored from ${new Date(batch.reset_at).toLocaleDateString()}`);
            load();
            onRestored?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to restore reset batch");
        } finally {
            setBusyBatchId(null);
        }
    };

    if (loading) return null;
    if (rows.length === 0) return null;

    return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4 mt-6" data-testid="admin-reset-history-strip">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.06em] text-amber-800 mb-3">
                <ArrowCounterClockwise size={13} weight="duotone" />
                Reset history · {rows.length} {rows.length === 1 ? "reset" : "resets"}
            </div>
            <div className="space-y-2">
                {rows.slice(0, 6).map((b) => {
                    const total = Object.values(b.counts || {}).reduce((a, n) => a + n, 0);
                    const status = b.status || "active";
                    const isActive = status === "active";
                    return (
                        <div
                            key={b.id}
                            className="flex items-start gap-3 rounded-xl bg-card border border-amber-200/80 p-3"
                            data-testid={`admin-reset-batch-${b.id}`}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                                    <span>{fmtDateTime(b.reset_at)}</span>
                                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                        status === "restored"
                                            ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                            : status === "purged"
                                                ? "bg-slate-200 text-slate-700 border border-slate-300"
                                                : "bg-amber-100 text-amber-800 border border-amber-200"
                                    }`}>
                                        {status}
                                    </span>
                                </div>
                                <div className="text-[12px] text-muted-foreground mt-0.5">
                                    <span className="font-medium text-foreground">{b.admin_name || "Admin"}</span>
                                    {" · "}
                                    Archived {total} row{total === 1 ? "" : "s"} across {Object.keys(b.counts || {}).length} collection{Object.keys(b.counts || {}).length === 1 ? "" : "s"}
                                </div>
                                {b.reason && (
                                    <div className="text-[12px] text-slate-700 italic mt-1 leading-snug">
                                        &ldquo;{b.reason}&rdquo;
                                    </div>
                                )}
                                {isActive && b.expires_at && (
                                    <div className="text-[11px] text-amber-800/80 mt-1">
                                        Restorable until {fmtDate(b.expires_at)}
                                    </div>
                                )}
                            </div>
                            {isActive && b.restorable && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => restore(b)}
                                    disabled={busyBatchId === b.id}
                                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 shrink-0"
                                    data-testid={`admin-reset-restore-${b.id}`}
                                >
                                    <ArrowCounterClockwise size={12} className="mr-1" />
                                    {busyBatchId === b.id ? "Restoring…" : "Restore"}
                                </Button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

